import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AssetTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { Badge as CategoryBadge } from '@/components/category/badge'
import { AssetTypeFilters, AssetTypeOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AssetTypesQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.assetTypes.pageInfo, onIndex, basePath: '/asset-types' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AssetTypeFilters, AssetTypeOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assetTypes.nodes = dedupe([
              ...previousData.assetTypes.nodes,
              ...fetchMoreResult.assetTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Image' },
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Category' },
          { name: 'Warehouse availability' },
        ]}
        rows={data.assetTypes.nodes.map(assetType => (
          {
            id: assetType.id,
            columns: [
              assetType.image ? <img className="size-12 object-contain" src={assetType.image.url} /> : '',
              assetType.name,
              (
                <div className="flex flex-wrap gap-2">
                  { assetType.categories.map(c => <CategoryBadge key={c.id} category={c} />) }
                </div>
              ),
              assetType.availableToEvents ? 'Available' : 'Not Available'

            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
